import { useEffect, useState } from "react";
import { Phone, ChevronLeft, ChevronRight, Truck } from "lucide-react";
import { COMPANY, TRUCK_IMAGES } from "../lib/constants";
import useReveal from "../hooks/useReveal";

const AUTOPLAY_MS = 5000;

export default function OnTheRoad() {
  const head = useReveal();
  const gallery = useReveal();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const total = TRUCK_IMAGES.length;
  const next = () => setActive((i) => (i + 1) % total);
  const prev = () => setActive((i) => (i - 1 + total) % total);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((i) => (i + 1) % total), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, total]);

  return (
    <section
      id="on-the-road"
      data-testid="on-the-road-section"
      className="py-20 md:py-28 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — Image carousel */}
          <div
            ref={gallery.ref}
            className={`fade-in-up ${gallery.visible ? "is-visible" : ""} lg:col-span-7 order-1 lg:order-1`}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-[#00B4D8]/25 to-[#0A192F]/15 rounded-3xl blur-2xl" />

              {/* Main image */}
              <div
                data-testid="truck-carousel"
                className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-[0_18px_50px_rgb(10,25,47,0.14)] bg-[#0A192F]"
              >
                <div className="aspect-[4/3] sm:aspect-[16/11] lg:aspect-[5/4]">
                  {TRUCK_IMAGES.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Y&Y Cleaning service vehicle ${i + 1}`}
                      loading={i === 0 ? "eager" : "lazy"}
                      data-testid={`truck-image-${i}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-all duration-[900ms] ease-out ${
                        i === active
                          ? "opacity-100 scale-100"
                          : "opacity-0 scale-[1.04]"
                      } hover:brightness-110 hover:scale-[1.02]`}
                    />
                  ))}
                </div>

                {/* Controls */}
                <button
                  type="button"
                  data-testid="truck-prev"
                  onClick={prev}
                  aria-label="Previous truck photo"
                  className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white border border-white/40 shadow-lg backdrop-blur flex items-center justify-center text-[#0A192F] transition-all hover:-translate-x-0.5"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  data-testid="truck-next"
                  onClick={next}
                  aria-label="Next truck photo"
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white border border-white/40 shadow-lg backdrop-blur flex items-center justify-center text-[#0A192F] transition-all hover:translate-x-0.5"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Badge */}
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0A192F]/85 backdrop-blur-md text-white px-3.5 py-1.5 rounded-full border border-white/10">
                  <Truck className="w-3.5 h-3.5 text-[#00B4D8]" />
                  <span className="text-[11px] font-bold tracking-[0.18em] uppercase">
                    Mobile Service Unit
                  </span>
                </div>

                {/* Counter / dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#0A192F]/70 backdrop-blur-md px-3 py-2 rounded-full border border-white/10">
                  {TRUCK_IMAGES.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`truck-dot-${i}`}
                      onClick={() => setActive(i)}
                      aria-label={`Show photo ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${
                        i === active
                          ? "w-6 bg-[#00B4D8]"
                          : "w-1.5 bg-white/40 hover:bg-white/70"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Thumbnail strip */}
              <div className="mt-4 grid grid-cols-4 gap-2 sm:gap-3">
                {TRUCK_IMAGES.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`truck-thumb-${i}`}
                    onClick={() => setActive(i)}
                    aria-label={`Select photo ${i + 1}`}
                    className={`relative overflow-hidden rounded-xl border-2 transition-all aspect-[4/3] group ${
                      i === active
                        ? "border-[#00B4D8] shadow-md"
                        : "border-transparent hover:border-slate-300 opacity-75 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — Text content */}
          <div
            ref={head.ref}
            className={`fade-in-up ${head.visible ? "is-visible" : ""} lg:col-span-5 order-2 lg:order-2`}
          >
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00B4D8]">
              Mobile Service
            </span>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] text-[#0A192F] tracking-tight">
              On the Road.{" "}
              <span className="text-[#00B4D8]">Ready to Serve.</span>
            </h2>
            <p className="mt-5 text-lg lg:text-xl font-heading font-semibold text-[#0A192F]/80 leading-snug">
              Our fully equipped service vehicle allows us to deliver fast,
              reliable, and professional service directly to your location.
            </p>
            <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">
              We take pride in showing up prepared, organized, and ready to get
              the job done right the first time. Whether it's a residential or
              commercial project, our mobile team ensures efficiency, quality,
              and a seamless experience from start to finish.
            </p>

            <div className="mt-9">
              <a
                data-testid="ontheroad-call-btn"
                href={`tel:${COMPANY.phoneTel}`}
                className="group inline-flex items-center justify-center gap-2.5 bg-[#0A192F] hover:bg-[#00B4D8] text-white px-8 py-4 rounded-full font-semibold text-base shadow-lg shadow-[#0A192F]/20 hover:shadow-cyan-500/40 transition-all duration-300 hover:-translate-y-1"
              >
                <Phone className="w-5 h-5" />
                Call Us Today
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
